import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, ErrorBoundary, SEOHelmet, Header, FlashingBanner } from '@/components/layout';
import { Toaster } from '@/components/ui/toaster';

// Critical above-fold — eager loaded
import Hero from '@/components/sections/Hero';

// Below-fold — lazy loaded for faster initial paint
const About = lazy(() => import('@/components/sections/About'));
const Courses = lazy(() => import('@/components/sections/Courses'));
const Insights = lazy(() => import('@/components/sections/Insights'));
const Register = lazy(() => import('@/components/sections/Register'));
const Contact = lazy(() => import('@/components/sections/Contact'));
const Footer = lazy(() => import('@/components/layout/Footer'));
const ChatWidget = lazy(() => import('@/components/chat/ChatWidget'));

// Admin pages — lazy loaded (only fetched when /admin routes are visited)
const AdminLogin     = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Minimal inline skeleton — avoids layout shift while lazy chunks load
const SectionSkeleton = () => (
  <div className="py-24 flex items-center justify-center" aria-hidden>
    <div className="w-8 h-8 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
  </div>
);

// Full-screen skeleton for admin pages
const PageSkeleton = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
  </div>
);

// ── Main public site layout ───────────────────────────────────────────────────
const SiteLayout = () => (
  <>
    <SEOHelmet />
    <div className="min-h-screen bg-background text-foreground">

      <ErrorBoundary><Header /></ErrorBoundary>
      <FlashingBanner />

      <main id="main-content">
        <ErrorBoundary><Hero /></ErrorBoundary>

        <Suspense fallback={<SectionSkeleton />}>
          <ErrorBoundary><About /></ErrorBoundary>
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <ErrorBoundary title="Courses unavailable" description="We could not load the courses section. Please refresh.">
            <Courses />
          </ErrorBoundary>
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <ErrorBoundary><Insights /></ErrorBoundary>
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <ErrorBoundary title="Registration unavailable" description="The registration form encountered an error. Please try refreshing or contact us directly.">
            <Register />
          </ErrorBoundary>
        </Suspense>

        <Suspense fallback={<SectionSkeleton />}>
          <ErrorBoundary><Contact /></ErrorBoundary>
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <ErrorBoundary><Footer /></ErrorBoundary>
      </Suspense>

      <Suspense fallback={null}>
        <ErrorBoundary><ChatWidget /></ErrorBoundary>
      </Suspense>
    </div>
  </>
);

// ── App root ─────────────────────────────────────────────────────────────────
function App() {
  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <Routes>
          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <AdminDashboard />
              </Suspense>
            }
          />

          {/* Public site — catches all other paths */}
          <Route path="/*" element={<SiteLayout />} />
        </Routes>

        <Toaster />
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
